import { ChangeEvent, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'

import { yupResolver } from '@hookform/resolvers/yup'

import { EmailVerificationFormValues } from '@/enums/forms'
import { emailVerificationFieldsKeys } from '@/utils/formsUtils'

import { ButtonSpinner, PrimaryButton } from '@/components/buttons'
import { CustomInput } from '@/components/input'
import { InputErrorMessage } from '@/components/inputErrorMessage'
import { PageTitle } from '@/components/pageTitle'
import { TextHTML } from '@/components/typography'

import { validationSchema } from '@/validation/emailVerificationFormSchema'

import { TEmailVerificationFormValues } from '../types'

export type TCodeFormProps = {
  email?: string | null
  title: string
  formDescription: string | ((email: string) => string)
  buttonName: string
  isLoading: boolean
  inputPlaceholder: string
  errorMessage?: string
  onSubmit: SubmitHandler<TEmailVerificationFormValues>
}
export const CodeForm = ({
  email,
  title,
  formDescription,
  buttonName,
  isLoading,
  inputPlaceholder,
  errorMessage,
  onSubmit,
}: TCodeFormProps) => {
  let preparedFormDescription: string | undefined
  if (formDescription && typeof formDescription === 'function') {
    preparedFormDescription = formDescription(email || 'your email')
  } else if (formDescription) {
    preparedFormDescription = formDescription
  }

  const [isFocus, setFocus] = useState(false)

  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    getValues,
    trigger,
    formState: { errors },
    watch,
  } = useForm<TEmailVerificationFormValues>({
    resolver: yupResolver(validationSchema),
    mode: 'all',
  })
  const code = watch(EmailVerificationFormValues.code)
  const [codeValue] = getValues(emailVerificationFieldsKeys)

  const handleFocus = async () => {
    setFocus((prevState) => !prevState)
    if (codeValue?.length) {
      await trigger(EmailVerificationFormValues.code)
    }
  }

  return (
    <>
      <PageTitle title={title} />
      {preparedFormDescription && (
        <TextHTML className="page-description" title={preparedFormDescription} />
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name={'code'}
          control={control}
          render={({ field: { ref } }) => {
            const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
              if (errors.code) {
                clearErrors(EmailVerificationFormValues.code)
              }
              event?.target?.value &&
                setValue(EmailVerificationFormValues.code, event?.target?.value)
            }

            return (
              <CustomInput
                name={'code'}
                type={'text'}
                ref={ref}
                disabled={isLoading}
                readOnly={isLoading}
                isError={Boolean(errors?.code?.message)}
                isFocus={isFocus}
                onChange={handleChange}
                placeholder={inputPlaceholder}
                onFocus={handleFocus}
                onBlur={handleFocus}
                spellCheck={false}
                autoCorrect="off"
                isValid={!Boolean(errors?.code?.message) && !isFocus && Boolean(codeValue?.length)}
              />
            )
          }}
        />
        <InputErrorMessage
          rowNumber={1}
          message={errors?.code?.message ? errors.code.message : errorMessage}
        />
        <div className="button-container">
          <PrimaryButton
            type="submit"
            disabled={isLoading || !code?.length || Boolean(errors?.code?.message)}
          >
            {isLoading ? <ButtonSpinner /> : buttonName}
          </PrimaryButton>
        </div>
      </form>
    </>
  )
}
